// packages/web-ui/src/components/tutorial/TutorialGate.tsx
//
// Mounts the tutorial offer dialog + spotlight tour next to the AppShell.
// Behavior:
//   - On every login (auth user becomes non-null) AND after the first-launch
//     onboarding wizard has completed, the gate calls `offerIfNeeded(userId)`
//     once. The slice opens the prompt unless the user has dismissed forever
//     or has already been offered this session.
//   - The Help menu's "Start tutorial" item bypasses the prompt and starts
//     the tour directly via `useTutorialStore.startTour()`.
//
// The onboarding-complete gate is required because OnboardingGate always
// renders its children behind the takeover overlay — without this check, a
// first-time user would see the tutorial prompt stacked on top of the
// onboarding wizard.

import type { JSX } from 'react';
import { useEffect } from 'react';
import { useStore, type StoreApi } from 'zustand';
import { useAuthStore } from '../../state/auth.js';
import type { OnboardingSlice } from '../../state/onboarding.js';
import { useTutorialStore } from '../../state/tutorial.js';
import { TutorialPromptDialog } from './TutorialPromptDialog.js';
import { TutorialTour } from './TutorialTour.js';

export interface TutorialGateProps {
  /** Onboarding store — the gate waits until onboarding is `completed`. */
  onboardingStore: StoreApi<OnboardingSlice>;
}

export function TutorialGate({ onboardingStore }: TutorialGateProps): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const onboardingCompleted = useStore(onboardingStore, (s) => s.completed);
  const promptOpen = useTutorialStore((s) => s.promptOpen);
  const offerIfNeeded = useTutorialStore((s) => s.offerIfNeeded);
  const acceptOffer = useTutorialStore((s) => s.acceptOffer);
  const declineOffer = useTutorialStore((s) => s.declineOffer);

  const userId = user?.id ?? null;

  useEffect(() => {
    if (userId === null) return;
    if (!onboardingCompleted) return;
    offerIfNeeded(userId);
  }, [userId, onboardingCompleted, offerIfNeeded]);

  return (
    <>
      <TutorialPromptDialog
        open={promptOpen}
        onShow={(dontShowAgain) => {
          if (userId !== null) acceptOffer(userId, dontShowAgain);
        }}
        onSkip={(dontShowAgain) => {
          if (userId !== null) declineOffer(userId, dontShowAgain);
        }}
      />
      <TutorialTour />
    </>
  );
}
